import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { AccountService } from "@/services/AccountService"
import { BillRequestService } from "@/services/BillRequestService"
import { MeterpointService } from "@/services/MeterpointService"
import { getUnderReviewPeriods } from "./getUnderReviewPeriods"
import { COOLING_OFF_PERIOD } from "@/constants/finance"
import {
  REQUIRE_READING_NEWER_THAN_DAYS,
  REQUIRE_SMART_READING_NEWER_THAN_DAYS,
} from "@/constants/meterpoints"
import { daysFromToday } from "@/helpers/dateHelper"
import dayjs from "dayjs"

export enum BillIssue {
  RECEIVED_BILL_TODAY = "RECEIVED_BILL_TODAY",
  IS_COOLING_OFF = "IS_COOLING_OFF",
  REQUIRES_READINGS = "REQUIRES_READINGS",
  ACCOUNT_INACTIVE = "ACCOUNT_INACTIVE",
  NO_OPENING_READS = "NO_OPENING_READS",
  HAS_FAILED_BILL = "HAS_FAILED_BILL",
  ACCOUNT_IN_REVIEW = "ACCOUNT_IN_REVIEW",
}

export interface IBillEligibility {
  issues: BillIssue[]
}

export const getBillEligibility = (accountNumber: string) => {
  const queries = new QueryAggregator()
  const issues: BillIssue[] = []

  const accountQuery = AccountService.getAccount(accountNumber)
  queries.add(accountQuery)

  const coreIdQuery = AccountService.getCoreIdByAccountNumber(accountNumber)
  queries.add(coreIdQuery)

  const billRequestQuery = BillRequestService.getForAccount(coreIdQuery.data)
  queries.add(billRequestQuery)

  const meterpointsQuery = MeterpointService.getForAccount(accountNumber)
  queries.add(meterpointsQuery)

  const reviewPeriodsQuery = getUnderReviewPeriods(accountNumber)
  queries.add(reviewPeriodsQuery)

  const latestValidBillRequestDttm = billRequestQuery.data
    ?.filter((bill) => bill.statusCode !== "Failed")
    ?.sort((a, b) =>
      a.taskCreatedDttm.isAfter(b.taskCreatedDttm) ? -1 : 1,
    )[0]?.taskCreatedDttm

  const startDate = dayjs(accountQuery.data.startDate)

  const isReadOld = (date, thresholdDays) =>
    dayjs().diff(date, "days") > thresholdDays

  const requiresReadings = meterpointsQuery.data.some((meterpointDetail) => {
    return meterpointDetail.hasSmartMeter
      ? isReadOld(
          meterpointDetail.latestRead?.date,
          REQUIRE_SMART_READING_NEWER_THAN_DAYS,
        )
      : isReadOld(
          meterpointDetail.latestRead?.date,
          REQUIRE_READING_NEWER_THAN_DAYS,
        )
  })

  const hasNoOpeningReads = meterpointsQuery.data.some(
    (meterpoint) =>
      !meterpoint.readings?.some((reading) => reading.isOfTypeFirst),
  )

  const hasFailedBill = billRequestQuery.data.some((bill) => bill.isFailed)

  const accountInReview = reviewPeriodsQuery.data.some(
    (period) => period.isActive,
  )

  if (
    latestValidBillRequestDttm &&
    daysFromToday(latestValidBillRequestDttm) === 0
  ) {
    issues.push(BillIssue.RECEIVED_BILL_TODAY)
  }
  if (daysFromToday(startDate) <= COOLING_OFF_PERIOD) {
    issues.push(BillIssue.IS_COOLING_OFF)
  }
  if (requiresReadings) {
    issues.push(BillIssue.REQUIRES_READINGS)
  }
  if (!accountQuery.data.isActive) {
    issues.push(BillIssue.ACCOUNT_INACTIVE)
  }
  if (hasNoOpeningReads) {
    issues.push(BillIssue.NO_OPENING_READS)
  }
  if (hasFailedBill) {
    issues.push(BillIssue.HAS_FAILED_BILL)
  }
  if (accountInReview) {
    issues.push(BillIssue.ACCOUNT_IN_REVIEW)
  }

  const data: IBillEligibility = {
    issues: issues,
  }

  return queries.setData(data).getAggregate()
}
